import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Camera, Upload, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { compressImage, formatFileSize, validateImageFile, CompressionStats } from '@/utils/imageCompression';
import { hashImage } from '@/utils/imageHash';
import { BookshelfScanResult } from '@/components/BookshelfScanResult';
import { cn } from '@/lib/utils';

interface MatchedBook {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
}

interface ScanResult {
  books: Array<{
    extractedTitle: string;
    extractedAuthor: string;
    extractionConfidence: number;
    matchedBook?: MatchedBook;
    matchConfidence?: number;
    isMatched: boolean;
    alreadyInLibrary?: boolean;
    existingShelf?: 'to-read' | 'reading' | 'read' | 'unfinished';
    existingRating?: number;
    userBookId?: string;
  }>;
  totalExtracted: number;
  totalMatched: number;
  scanNumber: number;
  scansRemaining: number;
}

export default function OnboardingBookshelfScan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Auth guard
  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);
  const [compressionStats, setCompressionStats] = useState<CompressionStats | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [currentScanNumber, setCurrentScanNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [processedBooksCount, setProcessedBooksCount] = useState(0);

  const handleFileSelect = async (file: File) => {
    setError(null);
    
    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      toast.error(validation.error);
      return;
    }

    try {
      // Compress image
      const result = await compressImage(file);
      setSelectedImage(URL.createObjectURL(result.blob));
      setCompressionStats(result.stats);
      toast.success(`Image compressed: ${result.stats.reductionPercent}% reduction`);
    } catch (error) {
      console.error('Compression error:', error);
      setError('Failed to compress image');
      toast.error('Failed to compress image');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleScanBooks = async () => {
    if (!selectedImage || !user) return;

    setIsScanning(true);
    setError(null);
    setScanResult(null);

    try {
      // Re-compress to get base64
      const response = await fetch(selectedImage);
      const blob = await response.blob();
      const result = await compressImage(blob);
      
      // Generate hash
      const imageHash = await hashImage(result.base64);

      // Call edge function
      const { data, error } = await supabase.functions.invoke('scan-bookshelf', {
        body: {
          imageBase64: result.base64,
          imageHash,
        },
      });

      if (error) {
        console.error('Scan error:', error);
        
        // Try to extract error from context
        const errorData = error.context?.body || data;
        const errorType = errorData?.error || '';
        const errorMessage = errorData?.message || '';
        
        if (errorType === 'SCAN_LIMIT_REACHED' || errorMessage.includes('used all')) {
          setError("You've used all 5 bookshelf scans");
          toast.error("You've used all 5 scans");
        } else if (errorType === 'DUPLICATE_SCAN' || errorMessage.includes('already been scanned')) {
          setError('This photo has already been scanned. Please try a different photo.');
          toast.error('This photo was already scanned');
        } else if (errorType === 'NO_BOOKS_DETECTED' || errorMessage.includes('No books detected')) {
          setError('No books detected. Try better lighting or a different angle.');
          toast.error('No books detected');
        } else {
          setError(errorMessage || 'Failed to scan bookshelf');
          toast.error(errorMessage || 'Failed to scan bookshelf');
        }
        return;
      }

      setScanResult(data);
      setCurrentScanNumber(data.scanNumber);
      toast.success(`Found ${data.books.length} books!`);
    } catch (error) {
      console.error('Unexpected scan error:', error);
      setError('An unexpected error occurred');
      toast.error('Failed to scan bookshelf');
    } finally {
      setIsScanning(false);
    }
  };

  const handleTakeAnother = () => {
    setSelectedImage(null);
    setCompressionStats(null);
    setScanResult(null);
    setError(null);
  };

  const handleContinue = () => {
    if (processedBooksCount > 0) {
      toast.success(`Added ${processedBooksCount} books to your library!`);
    }
    navigate('/onboarding/genres');
  };

  const handleBooksProcessed = () => {
    setProcessedBooksCount(prev => prev + 1);
  };

  const handleSkip = () => {
    navigate('/onboarding/genres');
  };

  const canTakeMore = currentScanNumber < 5;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full mx-auto flex items-center justify-center mb-4">
            <Camera className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2">
            Scan Your Bookshelf
          </h1>
          <p className="text-muted-foreground">
            Take up to 5 photos to quickly add books to your library
          </p>
          <Badge variant="secondary" className="mt-2">
            Scan {currentScanNumber} of 5
          </Badge>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">
              {selectedImage ? 'Preview' : 'Upload Photo'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedImage ? (
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="h-32 flex-col gap-2"
                  onClick={handleCameraClick}
                >
                  <Camera className="w-8 h-8" />
                  <span>Take Photo</span>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-32 flex-col gap-2"
                  onClick={handleUploadClick}
                >
                  <Upload className="w-8 h-8" />
                  <span>Upload Image</span>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden border">
                  <img
                    src={selectedImage}
                    alt="Bookshelf preview"
                    className="w-full h-auto"
                  />
                </div>
                
                {compressionStats && (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>
                      <strong>Original:</strong> {formatFileSize(compressionStats.originalSize)} 
                      ({compressionStats.originalDimensions.width}×{compressionStats.originalDimensions.height})
                    </p>
                    <p>
                      <strong>Compressed:</strong> {formatFileSize(compressionStats.compressedSize)} 
                      ({compressionStats.finalDimensions.width}×{compressionStats.finalDimensions.height})
                    </p>
                    <p className="text-success">
                      <strong>{compressionStats.reductionPercent}% size reduction</strong>
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={handleScanBooks}
                    disabled={isScanning}
                    className="flex-1"
                  >
                    {isScanning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      'Scan Books'
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleTakeAnother}>
                    Change Photo
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {scanResult && (
          <div className="mb-6">
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-center">
                📚 Found {scanResult.totalExtracted} books, matched {scanResult.totalMatched} to our database!
                {scanResult.totalMatched > 0 && ' Rate them to get personalized rankings.'}
              </p>
            </div>
            <BookshelfScanResult 
              books={scanResult.books}
              onBooksProcessed={handleBooksProcessed}
            />
          </div>
        )}

        <div className="flex flex-col gap-3">
          {scanResult && canTakeMore && (
            <Button onClick={handleTakeAnother} variant="outline" size="lg">
              <Camera className="w-5 h-5 mr-2" />
              Take Another Photo ({5 - currentScanNumber} remaining)
            </Button>
          )}
          
          <div className="flex gap-3">
            <Button onClick={handleSkip} variant="outline" className="flex-1">
              Skip This Step
            </Button>
            <Button onClick={handleContinue} size="lg" className="flex-1">
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />
      </div>
    </div>
  );
}
